import { Injectable, inject } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { OpportunityFilesService } from '@/services/opportunity-files.service';
import * as OpportunityFilesActions from './opportunity-files.actions';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class OpportunityFilesEffects {
  private actions$ = inject(Actions);

  constructor(private opportunityFilesService: OpportunityFilesService) {}

  // Effect to handle loading opportunity files
  loadOpportunityFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityFilesActions.loadOpportunityFiles),
      mergeMap((action) =>
        this.opportunityFilesService.getFiles(action.opportunityId, action.category).pipe(
          map((files) => OpportunityFilesActions.loadOpportunityFilesSuccess({ files })),
          catchError((error) => of(OpportunityFilesActions.loadOpportunityFilesFailure({ error })))
        )
      )
    )
  );

  // Effect to handle creating opportunity files
  createOpportunityFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityFilesActions.createOpportunityFile),
      mergeMap((action) =>
        this.opportunityFilesService.createFile(action.opportunityId, action.dto).pipe(
          map((file) => OpportunityFilesActions.createOpportunityFileSuccess({ file })),
          catchError((error) => of(OpportunityFilesActions.createOpportunityFileFailure({ error })))
        )
      )
    )
  );

  // Effect to handle updating opportunity files
  updateOpportunityFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityFilesActions.updateOpportunityFile),
      mergeMap((action) =>
        this.opportunityFilesService.updateFile(action.opportunityId, action.fileId, action.dto).pipe(
          map((file) => OpportunityFilesActions.updateOpportunityFileSuccess({ file })),
          catchError((error) => of(OpportunityFilesActions.updateOpportunityFileFailure({ error })))
        )
      )
    )
  );

  // Effect to handle bulk updating opportunity files
  bulkUpdateOpportunityFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityFilesActions.bulkUpdateOpportunityFiles),
      mergeMap((action) =>
        this.opportunityFilesService.bulkUpdateFiles(action.opportunityId, action.dto).pipe(
          map((files) => OpportunityFilesActions.bulkUpdateOpportunityFilesSuccess({ files })),
          catchError((error) => of(OpportunityFilesActions.bulkUpdateOpportunityFilesFailure({ error })))
        )
      )
    )
  );

  // Effect to handle deleting opportunity files
  deleteOpportunityFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityFilesActions.deleteOpportunityFile),
      mergeMap((action) =>
        this.opportunityFilesService.deleteFile(action.opportunityId, action.file.id).pipe(
          map(() => OpportunityFilesActions.deleteOpportunityFileSuccess({ file: action.file })),
          catchError((error) => of(OpportunityFilesActions.deleteOpportunityFileFailure({ error })))
        )
      )
    )
  );

  // Effect to handle updating opportunity file category
  updateOpportunityFileCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OpportunityFilesActions.updateOpportunityFileCategory),
      mergeMap((action) =>
        this.opportunityFilesService.updateFile(action.opportunityId, action.fileId, { category: action.category }).pipe(
          map((file) => OpportunityFilesActions.updateOpportunityFileCategorySuccess({ file })),
          catchError((error) => of(OpportunityFilesActions.updateOpportunityFileCategoryFailure({ error })))
        )
      )
    )
  );
}
